import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'

import { getIntegrationDisplayName, IntegrationServiceId } from '../../../common/integrations'

import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import { DocumentProspectingStep, DocumentProspectingStepProps } from '../../../models/DocumentProspecting'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  clearDocumentProspectingDetailsForm,
  selectDocumentProspectingDetailsFormLoading,
  selectDocumentProspectingServiceId,
  selectDocumentProspectingSuccessIntegrations,
  setDocumentProspectingServiceId,
} from '../../../store/processDocument/documentProspecting'

import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { Button } from '../../../components/Button/Button'
import { useDocumentProspectingEligibleIntegrations } from './hooks'

const _CURRENT_STEP = DocumentProspectingStep.INTEGRATION

export const ProspectingIntegrationSelector: React.FC<DocumentProspectingStepProps> = ({ activeStep, onActivate, onComplete }) => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const selectedServiceId = useAppSelector(selectDocumentProspectingServiceId)
  const detailsFormLoading = useAppSelector(selectDocumentProspectingDetailsFormLoading)
  const successIntegrations = useAppSelector(selectDocumentProspectingSuccessIntegrations)

  const isComplete = activeStep > _CURRENT_STEP

  const handleActive = useCallback(() => {
    if (detailsFormLoading) {
      return
    }
    onActivate(_CURRENT_STEP)
  }, [detailsFormLoading, onActivate])

  const _handleIntegrationOptionSelected = (serviceId: IntegrationServiceId) => {
    if (serviceId !== selectedServiceId) {
      dispatch(clearDocumentProspectingDetailsForm())
      dispatch(setDocumentProspectingServiceId(serviceId))
    }
    onComplete()
  }

  const handleIntegrationOptionSelected = tracked(
    _handleIntegrationOptionSelected,
    { eventName: TrackEvent.DOCUMENT_PROSPECTING_EXTERNAL_SYSTEM_SELECTED },
    { eventDetailsExtractor: (serviceId: IntegrationServiceId) => ({ targetSystem: serviceId }) },
  )

  const eligibleIntegrations = useDocumentProspectingEligibleIntegrations()

  const integrations = useMemo(
    () => eligibleIntegrations.filter(({ serviceId }) => !successIntegrations.includes(serviceId as IntegrationServiceId)),
    [eligibleIntegrations, successIntegrations],
  )

  return (
    <div className='d-flex flex-column gap-3'>
      <div className='d-flex gap-3 align-items-start'>
        <div className='z-1 border border-4 border-start-0 border-end-0 border-white'>
          <div
            className={classNames('document-prospecting-step-icon-wrapper rounded-2 d-flex align-items-center justify-content-center', {
              'zocks-border-color-gray-200 zocks-bg-color-gray-100 border': !isComplete,
              'zocks-bg-color-success-background': isComplete,
            })}
          >
            {isComplete ? <i className='bi bi-check zocks-text-success' /> : <i className='bi bi-bullseye' />}
          </div>
        </div>
        <div className='d-flex flex-column'>
          <div className='d-flex align-items-center document-prospecting-step-header-wrapper'>
            <h5>Save to external systems</h5>
          </div>
          {isComplete && selectedServiceId ? (
            <div className='d-flex align-items-center gap-1'>
              <span className='paragraph-small'>Contact will be created in Zocks and {getIntegrationDisplayName(selectedServiceId)}.</span>
              <div
                className={classNames('button-small p-0 border-0', {
                  'zocks-text-gray-500': detailsFormLoading,
                  'zocks-text-primary cursor-pointer': !detailsFormLoading,
                })}
                onClick={handleActive}
              >
                Change
              </div>
            </div>
          ) : (
            <span className='paragraph-small'>Contact will be created in Zocks. Choose an additional system.</span>
          )}
        </div>
      </div>
      {!isComplete ? (
        <div className='d-flex align-items-center gap-3 zocks-pl-3.0'>
          {integrations.map(({ serviceId }) => (
            <ProspectingIntegrationOption
              key={serviceId}
              serviceId={serviceId as IntegrationServiceId}
              onIntegrationOptionSelected={handleIntegrationOptionSelected}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

interface ProspectingIntegrationOptionProps {
  serviceId: IntegrationServiceId
  onIntegrationOptionSelected: (serviceId: IntegrationServiceId) => void
}

const ProspectingIntegrationOption: React.FC<ProspectingIntegrationOptionProps> = ({ serviceId, onIntegrationOptionSelected }) => {
  const integrationDisplayName = getIntegrationDisplayName(serviceId)

  const handleClick = () => {
    onIntegrationOptionSelected(serviceId)
  }

  return (
    <Button className='px-3 py-2 button-large d-flex align-items-center gap-2 border zocks-border-color-gray-300' onClick={handleClick}>
      <IntegrationIcon serviceId={serviceId} className='crm-icon' fill='var(--zocks-gray-900)' />
      <span className='paragraph-medium bold'>{integrationDisplayName}</span>
    </Button>
  )
}
